import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { formatSmartTimestamp } from "../../utils/format";
import { FiBookmark, FiMoreHorizontal, FiTrash2, FiHeart, FiMessageCircle, FiRepeat, FiX } from "react-icons/fi";
import { AuthContext } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { useConfirm } from "../../context/ConfirmContext";
import { Avatar, Dropdown, DropdownItem, Modal, Textarea } from "../ui";
import { toggleFavoritePost, toggleLikePost, repostPost, deletePost, addComment, deleteComment } from "../../api/posts";
import uploadUrl from "../../utils/assetUrl";

const PostCard = React.forwardRef(function PostCard({ post, author, isFavorited = false, onDeleted, repostedBy = null, highlighted = false }, ref) {
  const { user } = useContext(AuthContext);
  const { notify } = useToast();
  const confirm = useConfirm();
  const [saved, setSaved] = useState(isFavorited);
  const [likedByIds, setLikedByIds] = useState(post.likedByUserIds || []);
  const [comments, setComments] = useState(post.comments || []);
  const [reposts, setReposts] = useState(post.reposts || []);
  const [showComments, setShowComments] = useState(false);
  const [commentDraft, setCommentDraft] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [repostModal, setRepostModal] = useState(false);
  const [repostComment, setRepostComment] = useState("");
  const [reposting, setReposting] = useState(false);
  const [busy, setBusy] = useState(false);

  const isOwner = user?.id === post.userId;
  const authorName = author ? `${author.name}${author.lastName ? " " + author.lastName : ""}` : post.user?.name || "Alumni Member";
  const authorId = post.userId;
  const liked = likedByIds.includes(user?.id);
  const myRepost = reposts.find(r => r.userId === user?.id);

  const handleFavorite = async () => {
    setSaved(s => !s);
    try { await toggleFavoritePost(post.id); }
    catch { setSaved(s => !s); notify("Couldn't update bookmark.", "error"); }
  };

  const handleLike = async () => {
    const wasLiked = liked;
    setLikedByIds(prev => wasLiked ? prev.filter(id => id !== user.id) : [...prev, user.id]);
    try { await toggleLikePost(post.id); }
    catch { setLikedByIds(prev => wasLiked ? [...prev, user.id] : prev.filter(id => id !== user.id)); notify("Couldn't update like.", "error"); }
  };

  const handleRepost = async () => {
    if (myRepost) { notify("You've already reposted this.", "info"); return; }
    setReposting(true);
    try {
      const res = await repostPost(post.id, repostComment);
      setReposts(prev => [...prev, res.data.repost]);
      setRepostModal(false);
      setRepostComment("");
      notify("Reposted!", "success");
    } catch { notify("Couldn't repost.", "error"); }
    finally { setReposting(false); }
  };

  const handleDelete = async () => {
    const ok = await confirm({ title: "Delete this post?", description: "This post will be permanently deleted. This cannot be undone." });
    if (!ok) return;
    setBusy(true);
    try { await deletePost(post.id); notify("Post deleted.", "success"); onDeleted?.(post.id); }
    catch { notify("Couldn't delete this post.", "error"); }
    finally { setBusy(false); }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentDraft.trim()) return;
    setSubmittingComment(true);
    try {
      const res = await addComment(post.id, commentDraft.trim());
      setComments(prev => [...prev, res.data.comment]);
      setCommentDraft("");
    } catch { notify("Couldn't add comment.", "error"); }
    finally { setSubmittingComment(false); }
  };

  const handleDeleteComment = async (commentId) => {
    const ok = await confirm({ title: "Delete this comment?", description: "This comment will be permanently deleted. This cannot be undone." });
    if (!ok) return;
    try {
      await deleteComment(post.id, commentId);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch { notify("Couldn't delete comment.", "error"); }
  };

  return (
    <>
      <article ref={ref} className={`rounded-lg border bg-white p-4 shadow-card ${highlighted ? "ring-2 ring-primary-400" : ""} ${repostedBy ? "border-emerald-200" : "border-slate-200"}`}>
        {repostedBy && (
          <div className="mb-3 flex items-center gap-2 border-b border-emerald-100 pb-3 text-xs font-medium text-emerald-700">
            <FiRepeat className="h-3.5 w-3.5" />
            <Link to={`/profile/${repostedBy.id}`} className="hover:underline">
              {repostedBy.name} {repostedBy.lastName}
            </Link>
            reposted this{repostedBy.comment ? ":" : ""}
          </div>
        )}
        {repostedBy?.comment && (
          <p className="mb-3 whitespace-pre-wrap text-sm text-slate-700">{repostedBy.comment}</p>
        )}
        <div className="flex items-start justify-between gap-3">
          <Link to={`/profile/${authorId}`} className="flex items-start gap-3 hover:opacity-80">
            <Avatar name={authorName} size="md" />
            <div>
              <p className="text-sm font-semibold text-slate-900">{authorName}</p>
              <p className="text-xs text-slate-400">
                {post.createdAt ? formatSmartTimestamp(post.createdAt) : "just now"}
              </p>
            </div>
          </Link>
          {isOwner && (
            <Dropdown trigger={<button className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100" disabled={busy}><FiMoreHorizontal className="h-4 w-4" /></button>}>
              <DropdownItem icon={<FiTrash2 className="h-4 w-4" />} onClick={handleDelete} className="text-red-600 hover:bg-red-50 hover:text-red-700">Delete post</DropdownItem>
            </Dropdown>
          )}
        </div>

        {post.content && <p className="mt-3 whitespace-pre-wrap text-[15px] leading-relaxed text-slate-700">{post.content}</p>}
        {post.image && <img src={uploadUrl(post.image)} alt="" className="mt-3 max-h-[420px] w-full rounded-lg border border-slate-100 object-cover" />}

        <div className="mt-3 flex items-center gap-1 border-t border-slate-100 pt-3">
          <button onClick={handleLike} className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium transition-colors ${liked ? "text-red-500 bg-red-50" : "text-slate-500 hover:bg-slate-50"}`}>
            <FiHeart className={`h-4 w-4 ${liked ? "fill-red-500" : ""}`} /> {likedByIds.length > 0 && likedByIds.length}
          </button>
          <button onClick={() => setShowComments(s => !s)} className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-50">
            <FiMessageCircle className="h-4 w-4" /> {comments.length > 0 && comments.length}
          </button>
          <button onClick={() => setRepostModal(true)} className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium transition-colors ${myRepost ? "text-emerald-600 bg-emerald-50" : "text-slate-500 hover:bg-slate-50"}`}>
            <FiRepeat className="h-4 w-4" /> {reposts.length > 0 && reposts.length}
          </button>
          <button onClick={handleFavorite} className={`ml-auto flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium transition-colors ${saved ? "text-amber-600 bg-amber-50" : "text-slate-500 hover:bg-slate-50"}`}>
            <FiBookmark className={`h-4 w-4 ${saved ? "fill-amber-500" : ""}`} />
          </button>
        </div>

        {showComments && (
          <div className="mt-3 border-t border-slate-100 pt-3 space-y-3">
            {comments.map(c => (
              <div key={c.id} className="flex items-start gap-2">
                <Link to={`/profile/${c.userId}`}><Avatar name={c.user?.name || "?"} size="xs" /></Link>
                <div className="flex-1 rounded-lg bg-slate-50 px-3 py-2">
                  <p className="text-xs font-semibold text-slate-800">{c.user?.name} {c.user?.lastName}</p>
                  <p className="text-sm text-slate-700">{c.content}</p>
                </div>
                {(c.userId === user?.id || user?.roles === "admin") && (
                  <button onClick={() => handleDeleteComment(c.id)} className="text-slate-300 hover:text-red-400 mt-1"><FiX className="h-3.5 w-3.5" /></button>
                )}
              </div>
            ))}
            <form onSubmit={handleAddComment} className="flex gap-2 items-center">
              <Avatar name={user?.name || "?"} size="xs" />
              <input
                value={commentDraft}
                onChange={e => setCommentDraft(e.target.value)}
                placeholder="Add a comment…"
                className="flex-1 h-8 rounded-full border border-slate-200 bg-white px-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
              <button type="submit" disabled={submittingComment || !commentDraft.trim()} className="h-8 px-3 rounded-full bg-primary-600 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50">Post</button>
            </form>
          </div>
        )}
      </article>

      <Modal open={repostModal} onClose={() => setRepostModal(false)} title="Reshare this post"
        footer={<><button onClick={() => setRepostModal(false)} className="text-sm text-slate-500 hover:text-slate-700 px-4 py-2">Cancel</button><button onClick={handleRepost} disabled={reposting} className="px-4 py-2 rounded-lg bg-primary-600 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">{reposting ? "Sharing…" : "Share"}</button></>}>
        <div className="space-y-3">
          <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm text-slate-600">{post.content?.slice(0,100)}{post.content?.length > 100 ? "…" : ""}</div>
          <Textarea value={repostComment} onChange={e => setRepostComment(e.target.value)} placeholder="Add your thoughts (optional)…" rows={3} />
        </div>
      </Modal>
    </>
  );
});

export default PostCard;
